import { BottomSheet, Badge, Button, Card, CardHeader, SegmentedControl, formatDate, useMediaQuery } from "@helmr/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { IMPORT_CHUNK, fetchCategories, fetchImportContext, fetchPayees, importExpenses, type ImportRowInput } from "../../../api/expenses";
import { isApiError, newIdempotencyKey } from "../../../api/client";
import { useServices } from "../../../app/services";
import { Empty, Failure, Field, TableSkeleton, css as s, errorText, parseMoneyInput, type Scope } from "../../payroll/kit";
import { MODES } from "../NewExpensePage";
import m from "./import.module.css";
import {
  activeFilterCount, applyFilters, buildSuggester, clearFiltered, defaultFilters, findDuplicates, guessMode, guessPayee, isFiltered, isSelectable, selectAllFiltered, sortRows, summarise, toggle,
  type Filters, type Sort, type SortKey,
} from "./rowlogic";
import type { BadRow, Txn } from "./statement";

const PAGE = 100;
const PHONE = "(max-width: 767px)";
const SORTS: Array<{ value: string; label: string; sort: Sort }> = [
  { value: "date:asc", label: "Date, oldest first", sort: { key: "date", dir: "asc" } },
  { value: "date:desc", label: "Date, newest first", sort: { key: "date", dir: "desc" } },
  { value: "amount:desc", label: "Amount, high to low", sort: { key: "amount", dir: "desc" } },
  { value: "amount:asc", label: "Amount, low to high", sort: { key: "amount", dir: "asc" } },
  { value: "description:asc", label: "Description, A to Z", sort: { key: "description", dir: "asc" } },
  { value: "description:desc", label: "Description, Z to A", sort: { key: "description", dir: "desc" } },
];

interface RowEdit { categoryId?: string; payee?: string }
type Done = { kind: "created" | "duplicate"; expenseId: string | null };
interface Result { created: number; duplicates: number; failed: number; stopped: string | null }

export function Review({ name, txns, bad, sc, onChooseAnother }: { name: string; txns: Txn[]; bad: BadRow[]; sc: Scope; onChooseAnother: () => void }) {
  const { client } = useServices();
  const qc = useQueryClient();
  const phone = useMediaQuery(PHONE);
  const branchId = sc.monthBranchId;
  const range = useMemo(() => {
    const dates = txns.map((t) => t.date).sort();
    return { from: dates[0] ?? sc.today, to: dates[dates.length - 1] ?? sc.today };
  }, [txns, sc.today]);

  const cats = useQuery({ queryKey: ["expenses", "categories", ...sc.scopeKey], queryFn: () => fetchCategories(client), retry: false });
  const payees = useQuery({ queryKey: ["expenses", "payees", ...sc.scopeKey], queryFn: () => fetchPayees(client), retry: false });
  const [skipChecks, setSkipChecks] = useState(false);
  const ctx = useQuery({
    queryKey: ["expenses", "import-context", ...sc.scopeKey, branchId, range.from, range.to],
    queryFn: () => fetchImportContext(client, { branchId: branchId!, ...range }),
    enabled: !!branchId && !skipChecks, retry: false,
  });
  // Seeing past expenses needs `view`. A role that can only add still imports; the server skips repeats on its own.
  const noHistory = skipChecks || (isApiError(ctx.error) && ctx.error.status === 403);

  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [sortValue, setSortValue] = useState("date:asc");
  const sort = SORTS.find((x) => x.value === sortValue)!.sort;
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [edits, setEdits] = useState<Record<string, RowEdit>>({});
  const [done, setDone] = useState<ReadonlyMap<string, Done>>(new Map());
  const [failed, setFailed] = useState<ReadonlyMap<string, string>>(new Map());
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [limit, setLimit] = useState(PAGE);
  const [sheet, setSheet] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const keys = useRef(new Map<string, string>());

  useEffect(() => setLimit(PAGE), [filters, sortValue]);

  const liveCats = (cats.data?.categories ?? []).filter((c) => !c.archived);
  const livePayees = (payees.data?.payees ?? []).filter((p) => !p.archived);
  const payeeNames = useMemo(() => new Map(livePayees.map((p) => [p.id, p.name])), [livePayees]);
  const payeeIdByName = useMemo(() => new Map(livePayees.map((p) => [p.name.trim().toLowerCase(), p.id])), [livePayees]);
  const dups = useMemo(() => (ctx.data ? findDuplicates(txns, ctx.data.window) : new Map()), [ctx.data, txns]);
  const suggest = useMemo(() => buildSuggester(ctx.data?.history ?? [], payeeNames), [ctx.data, payeeNames]);

  /** What a row will be entered with: the user's edit, else the suggestion from past expenses, else a guess from the narration. */
  const effective = (t: Txn) => {
    const e = edits[t.id];
    const sug = suggest(t.description);
    const sugPayee = sug ? (sug.payeeId ? (payeeNames.get(sug.payeeId) ?? "") : (sug.payeeName ?? "")) : "";
    return {
      categoryId: e?.categoryId ?? sug?.categoryId ?? "",
      payee: e?.payee ?? (sugPayee || guessPayee(t.description) || ""),
      mode: guessMode(t.description, MODES),
      guessedCategory: e?.categoryId === undefined && !!sug?.categoryId,
    };
  };

  const filtered = useMemo(() => applyFilters(txns, filters), [txns, filters]);
  const sorted = useMemo(() => sortRows(filtered, sort), [filtered, sort]);
  const shown = sorted.slice(0, limit);
  const eligible = filtered.filter((t) => isSelectable(t) && !dups.has(t.id) && !done.has(t.id));
  const allEligibleOn = eligible.length > 0 && eligible.every((t) => selected.has(t.id));
  const sum = summarise(txns, selected);
  const needCategory = txns.filter((t) => selected.has(t.id) && effective(t).categoryId === "").length;

  const setEdit = (ids: Iterable<string>, patch: RowEdit) => setEdits((cur) => {
    const next = { ...cur };
    for (const id of ids) next[id] = { ...next[id], ...patch };
    return next;
  });
  const toggleAll = () => setSelected((cur) => (allEligibleOn ? clearFiltered(cur, filtered) : selectAllFiltered(cur, filtered, dups, new Set(done.keys()))));

  const submit = async () => {
    if (!branchId) return;
    const picked = txns.filter((t) => selected.has(t.id) && !done.has(t.id));
    const rows = picked.map((t): ImportRowInput => {
      const eff = effective(t);
      const payeeText = eff.payee.trim();
      const payeeId = payeeText ? payeeIdByName.get(payeeText.toLowerCase()) : undefined;
      return {
        date: t.date, amount: t.amount, narration: t.description, bankRef: t.ref ? t.ref.slice(0, 64) : null, categoryId: eff.categoryId, paymentMode: eff.mode,
        ...(payeeId ? { payeeId } : payeeText ? { payeeName: payeeText.slice(0, 120) } : {}),
      };
    });
    setBusy(true);
    setSubmitErr(null);
    const nextDone = new Map(done);
    const nextFailed = new Map<string, string>();
    const out: Result = { created: 0, duplicates: 0, failed: 0, stopped: null };
    try {
      for (let at = 0; at < rows.length; at += IMPORT_CHUNK) {
        const chunk = rows.slice(at, at + IMPORT_CHUNK);
        const body = { branchId, rows: chunk };
        // The same rows re-sent after a dropped connection reuse their key, so the server replays instead of redoing.
        const sig = JSON.stringify(body);
        const key = keys.current.get(sig) ?? newIdempotencyKey();
        keys.current.set(sig, key);
        const res = await importExpenses(client, body, key);
        for (const r of res.results) {
          const t = picked[at + r.index]!;
          if (r.status === "failed") { out.failed++; nextFailed.set(t.id, r.reason); } else if (r.status === "duplicate") { out.duplicates++; nextDone.set(t.id, { kind: "duplicate", expenseId: r.expenseId }); } else { out.created++; nextDone.set(t.id, { kind: "created", expenseId: r.expenseId }); }
        }
      }
    } catch (e) {
      out.stopped = errorText(e);
      setSubmitErr(out.stopped);
    } finally {
      setBusy(false);
    }
    setDone(nextDone);
    setFailed(nextFailed);
    setSelected((cur) => new Set([...cur].filter((id) => !nextDone.has(id))));
    setResult(out);
    if (out.created > 0) void qc.invalidateQueries({ queryKey: ["expenses"] });
  };

  if (!branchId) return <Card aria-label="Review"><div className={s.body}><p className={s.formErr}>Choose a branch in the switcher first.</p></div></Card>;
  if (cats.isPending || payees.isPending || (ctx.isPending && !noHistory)) return <Card aria-label="Checking statement"><CardHeader title={name} subtitle="Checking against your existing expenses…" /><TableSkeleton rows={8} label="Checking against your existing expenses" /></Card>;
  if (cats.error) return <Card aria-label="Review"><Failure error={cats.error} what="expense categories" onRetry={() => void cats.refetch()} /></Card>;
  if (payees.error) return <Card aria-label="Review"><Failure error={payees.error} what="payees" onRetry={() => void payees.refetch()} /></Card>;
  if (ctx.error && !noHistory) {
    return (
      <Card aria-label="Review">
        <Failure error={ctx.error} what="your existing expenses" onRetry={() => void ctx.refetch()} />
        <div className={s.actions} style={{ padding: "0 16px 16px" }}><Button onClick={() => setSkipChecks(true)}>Continue without the duplicate check</Button></div>
      </Card>
    );
  }

  const filterFields = <FilterFields filters={filters} set={setFilters} sc={sc} />;
  const counts = { debit: txns.filter((t) => t.direction === "debit").length, credit: txns.filter((t) => t.direction === "credit").length };

  return (
    <Card aria-label="Review transactions">
      <CardHeader title={name} subtitle={`${txns.length} transactions · ${counts.debit} debits · ${counts.credit} credits`} />
      {noHistory ? <p className={s.note} style={{ padding: "0 16px 8px" }}>{skipChecks ? "Duplicates were not checked." : "You can add expenses but not view them, so past expenses were not checked."} The server still never enters the same bank line twice.</p> : null}

      {result ? <ResultPanel result={result} failed={failed} txns={txns} sc={sc} onClose={() => setResult(null)} /> : null}

      <div className={m.bar}>
        <SegmentedControl label="Show" value={filters.direction} onChange={(direction) => setFilters({ ...filters, direction })} options={[{ value: "debit", label: "Debit" }, { value: "credit", label: "Credit" }, { value: "all", label: "All" }]} />
        {phone ? (
          <>
            <Field label="Search transactions">{(a) => <input {...a} className={s.input} type="search" value={filters.q} placeholder="Narration or ref" onChange={(e) => setFilters({ ...filters, q: e.target.value })} />}</Field>
            <Field label="Sort by">{(a) => <select {...a} className={s.input} value={sortValue} onChange={(e) => setSortValue(e.target.value)}>{SORTS.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}</select>}</Field>
            <Button onClick={() => setSheet(true)}>{`Filters${activeFilterCount(filters) ? ` (${activeFilterCount(filters)})` : ""}`}</Button>
          </>
        ) : filterFields}
        <div className={m.barEnd}>
          <Button size="sm" onClick={toggleAll} disabled={eligible.length === 0}>{allEligibleOn ? "Clear selection" : `Select all ${eligible.length}`}</Button>
          {isFiltered(filters) ? <Button size="sm" variant="plain" onClick={() => setFilters(defaultFilters())}>Reset filters</Button> : null}
        </div>
      </div>
      {phone ? (
        <BottomSheet open={sheet} onOpenChange={setSheet} title="Filters" footer={<Button variant="primary" onClick={() => setSheet(false)}>{`Show ${filtered.length} transactions`}</Button>}>
          <div className={m.sheetBody}>{filterFields}<Button variant="plain" onClick={() => setFilters(defaultFilters())}>Reset filters</Button></div>
        </BottomSheet>
      ) : null}

      {selected.size > 0 ? (
        <div className={m.bulk} role="group" aria-label="Change selected rows">
          <b>{selected.size} selected</b>
          <Field label="Category for selected">{(a) => <select {...a} className={s.input} value="" onChange={(e) => e.target.value && setEdit(selected, { categoryId: e.target.value })}><option value="">Choose…</option>{liveCats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>}</Field>
          <BulkPayee onApply={(payee) => setEdit(selected, { payee })} />
        </div>
      ) : null}
      <p className={s.note} style={{ padding: "8px 16px 0" }}>Fields with a dashed border are suggestions from your past expenses. Only money out can be entered as an expense.{dups.size > 0 ? ` ${dups.size} possible duplicate${dups.size === 1 ? "" : "s"} left unselected.` : ""}</p>

      <datalist id="payee-options">{livePayees.map((p) => <option key={p.id} value={p.name} />)}</datalist>

      {sorted.length === 0 ? (
        <Empty icon="receipt" title="No transactions match" description={txns.length === 0 ? "This statement has no readable transactions." : "Change or reset the filters to see more."} action={isFiltered(filters) ? <Button onClick={() => setFilters(defaultFilters())}>Reset filters</Button> : undefined} />
      ) : phone ? (
        <ul className={m.cards} aria-label="Transactions">
          {shown.map((t) => {
            const eff = effective(t);
            const isDone = done.get(t.id);
            return (
              <li key={t.id} className={m.card} data-selected={selected.has(t.id)} data-done={!!isDone}>
                <input type="checkbox" aria-label={`Select ${t.description}`} checked={selected.has(t.id)} disabled={!isSelectable(t) || !!isDone} title={isSelectable(t) ? undefined : "Money in is not an expense"} onChange={() => setSelected((c) => toggle(c, t.id))} />
                <div>
                  <span className={m.cardDate}>{formatDate(t.date, sc.locale, "short")}</span>
                  <Narration t={t} open={open.has(t.id)} onToggle={() => setOpen((c) => toggle(c, t.id))} />
                  <Flags dup={dups.get(t.id)} done={isDone} failed={failed.get(t.id)} />
                </div>
                <div className={m.cardAmt}><span className={t.direction === "debit" ? m.out : m.in}>{sc.money(t.amount)}</span><span className={m.sub}>{t.direction === "debit" ? "Debit" : "Credit"}</span></div>
                {isSelectable(t) && !isDone ? (
                  <div className={m.full}>
                    <RowFields t={t} eff={eff} cats={liveCats} onCategory={(categoryId) => setEdit([t.id], { categoryId })} onPayee={(payee) => setEdit([t.id], { payee })} />
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <div className={m.tableWrap}>
          <table className={m.tbl} aria-label="Statement transactions">
            <thead>
              <tr>
                <th className={m.cb}><input type="checkbox" aria-label="Select all filtered" checked={allEligibleOn} disabled={eligible.length === 0} onChange={toggleAll} /></th>
                <SortTh label="Date" k="date" sort={sort} onSort={(k) => setSortValue(nextSort(sort, k))} />
                <SortTh label="Description" k="description" sort={sort} onSort={(k) => setSortValue(nextSort(sort, k))} />
                <SortTh label="Amount" k="amount" sort={sort} onSort={(k) => setSortValue(nextSort(sort, k))} num />
                <th>Category</th>
                <th>Paid to</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((t) => {
                const eff = effective(t);
                const isDone = done.get(t.id);
                return (
                  <tr key={t.id} data-selected={selected.has(t.id)} data-done={!!isDone}>
                    <td className={m.cb}><input type="checkbox" aria-label={`Select ${t.description}`} checked={selected.has(t.id)} disabled={!isSelectable(t) || !!isDone} title={isSelectable(t) ? undefined : "Money in is not an expense"} onChange={() => setSelected((c) => toggle(c, t.id))} /></td>
                    <td className={m.num} style={{ textAlign: "left" }}>{formatDate(t.date, sc.locale, "short")}</td>
                    <td><Narration t={t} open={open.has(t.id)} onToggle={() => setOpen((c) => toggle(c, t.id))} /><Flags dup={dups.get(t.id)} done={isDone} failed={failed.get(t.id)} /></td>
                    <td className={m.num}><b className={t.direction === "debit" ? m.out : m.in}>{sc.money(t.amount)}</b><span className={m.sub}>{t.direction === "debit" ? "Debit" : "Credit"}</span></td>
                    {isSelectable(t) && !isDone ? <RowFields t={t} eff={eff} cats={liveCats} cells onCategory={(categoryId) => setEdit([t.id], { categoryId })} onPayee={(payee) => setEdit([t.id], { payee })} /> : <><td>—</td><td>—</td></>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {sorted.length > shown.length ? <div className={m.more}><Button onClick={() => setLimit((l) => l + PAGE)}>{`Show ${Math.min(PAGE, sorted.length - shown.length)} more (${sorted.length - shown.length} left)`}</Button></div> : null}
      {sorted.length > 0 ? <p className={s.note} style={{ padding: "0 16px 12px" }}>{`Showing ${shown.length} of ${sorted.length}${filtered.length !== txns.length ? ` filtered (${txns.length} in the file)` : ""}.`}</p> : null}

      {bad.length > 0 ? (
        <details style={{ padding: "0 16px 16px" }}>
          <summary className={s.note}>{`${bad.length} row${bad.length === 1 ? "" : "s"} in the file could not be read`}</summary>
          <ul className={s.list} aria-label="Rows that could not be read">{bad.map((b) => <li key={b.rowNo}><span>{`Row ${b.rowNo}: ${b.raw}`}</span><span>{b.reason}</span></li>)}</ul>
        </details>
      ) : null}

      <div className={m.footer} role="region" aria-label="Selection">
        <span className={m.tot} role="status">{sum.count === 0 ? "Nothing selected" : <><b>{sum.count}</b> selected · <b>{sc.money(sum.total)}</b></>}</span>
        {sum.count > 0 && needCategory > 0 ? <span className={m.need}>{`${needCategory} need${needCategory === 1 ? "s" : ""} a category`}</span> : null}
        {submitErr ? <span className={s.formErr} role="alert">{submitErr}</span> : null}
        <div className={m.grow}>
          <Button onClick={onChooseAnother}>Choose another file</Button>
          <Button variant="primary" disabled={sum.count === 0 || needCategory > 0} busy={busy} busyLabel="Entering…" onClick={() => void submit()}>{sum.count === 0 ? "Enter expenses" : `Enter ${sum.count} expense${sum.count === 1 ? "" : "s"}`}</Button>
        </div>
      </div>
    </Card>
  );
}

const nextSort = (cur: Sort, k: SortKey) => `${k}:${cur.key === k ? (cur.dir === "asc" ? "desc" : "asc") : k === "amount" ? "desc" : "asc"}`;

function SortTh({ label, k, sort, onSort, num }: { label: string; k: SortKey; sort: Sort; onSort: (k: SortKey) => void; num?: boolean }) {
  const on = sort.key === k;
  return (
    <th className={num ? m.numH : undefined} aria-sort={on ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" onClick={() => onSort(k)}>{label}{on ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}</button>
    </th>
  );
}

/** Long narrations are cut to one line; tapping shows all of it. */
function Narration({ t, open, onToggle }: { t: Txn; open: boolean; onToggle: () => void }) {
  return (
    <>
      <button type="button" className={m.narr} aria-expanded={open} title={t.description} onClick={onToggle}>{t.description}</button>
      {t.ref ? <span className={m.sub}>{`Ref ${t.ref}`}</span> : null}
    </>
  );
}

function Flags({ dup, done, failed }: { dup: { kind: string } | undefined; done: Done | undefined; failed: string | undefined }) {
  if (!dup && !done && !failed) return null;
  return (
    <div className={m.flag}>
      {done ? <Badge tone="ok">{done.kind === "created" ? "Entered" : "Already entered"}</Badge> : dup ? <Badge tone="warn" dot title={dup.kind === "reference" ? "An expense with this bank reference already exists" : "An expense with the same amount exists on a nearby date"}>Possible duplicate</Badge> : null}
      {failed ? <span className={m.reason} role="alert">{failed}</span> : null}
    </div>
  );
}

function RowFields({ t, eff, cats, cells, onCategory, onPayee }: {
  t: Txn; eff: { categoryId: string; payee: string; guessedCategory: boolean }; cats: Array<{ id: string; name: string }>; cells?: boolean; onCategory: (id: string) => void; onPayee: (p: string) => void;
}) {
  const category = (
    <select className={m.cell} aria-label={`Category for ${t.description}`} value={eff.categoryId} data-guess={eff.guessedCategory} title={eff.guessedCategory ? "Suggested from your past expenses" : undefined} onChange={(e) => onCategory(e.target.value)}>
      <option value="">Choose category</option>
      {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
    </select>
  );
  const payee = <input className={m.cell} list="payee-options" aria-label={`Paid to for ${t.description}`} value={eff.payee} maxLength={120} placeholder="Paid to" onChange={(e) => onPayee(e.target.value)} />;
  return cells ? <><td>{category}</td><td>{payee}</td></> : <>{category}{payee}</>;
}

function BulkPayee({ onApply }: { onApply: (p: string) => void }) {
  const [v, setV] = useState("");
  return (
    <>
      <Field label="Paid to for selected">{(a) => <input {...a} className={s.input} list="payee-options" value={v} maxLength={120} onChange={(e) => setV(e.target.value)} />}</Field>
      <Button size="sm" disabled={v.trim() === ""} onClick={() => { onApply(v.trim()); setV(""); }}>Apply payee</Button>
    </>
  );
}

function FilterFields({ filters, set, sc }: { filters: Filters; set: (f: Filters) => void; sc: Scope }) {
  // Amount boxes keep what the user typed; the filter holds the parsed minor units (null while empty or not yet a number).
  const [min, setMin] = useState("");
  const [max, setMax] = useState("");
  const parse = (v: string) => (v.trim() === "" ? null : parseMoneyInput(v, sc.currency, sc.locale));
  useEffect(() => { if (filters.min === null && min !== "" && parse(min) !== null) setMin(""); if (filters.max === null && max !== "" && parse(max) !== null) setMax(""); }, [filters.min, filters.max]); // eslint-disable-line react-hooks/exhaustive-deps
  const bad = (v: string) => (v.trim() !== "" && parse(v) === null ? "Enter an amount" : null);
  return (
    <>
      <Field label="From date">{(a) => <input {...a} className={s.input} type="date" value={filters.from} onChange={(e) => set({ ...filters, from: e.target.value })} />}</Field>
      <Field label="To date">{(a) => <input {...a} className={s.input} type="date" value={filters.to} onChange={(e) => set({ ...filters, to: e.target.value })} />}</Field>
      <Field label="Min amount" error={bad(min)}>{(a) => <input {...a} className={s.input} inputMode="decimal" value={min} onChange={(e) => { setMin(e.target.value); set({ ...filters, min: parse(e.target.value) }); }} />}</Field>
      <Field label="Max amount" error={bad(max)}>{(a) => <input {...a} className={s.input} inputMode="decimal" value={max} onChange={(e) => { setMax(e.target.value); set({ ...filters, max: parse(e.target.value) }); }} />}</Field>
      <Field label="Search transactions">{(a) => <input {...a} className={s.input} type="search" value={filters.q} placeholder="Narration or ref" onChange={(e) => set({ ...filters, q: e.target.value })} />}</Field>
    </>
  );
}

function ResultPanel({ result, failed, txns, sc, onClose }: { result: Result; failed: ReadonlyMap<string, string>; txns: Txn[]; sc: Scope; onClose: () => void }) {
  const rows = txns.filter((t) => failed.has(t.id));
  return (
    <div className={m.result} role="status" aria-label="Import result" ref={(el) => el?.scrollIntoView?.({ block: "nearest" })}>
      <div className={m.counts}>
        <Badge tone="ok">{`${result.created} entered`}</Badge>
        {result.duplicates > 0 ? <Badge tone="neutral">{`${result.duplicates} skipped, already entered`}</Badge> : null}
        {result.failed > 0 ? <Badge tone="crit">{`${result.failed} failed`}</Badge> : null}
      </div>
      {result.stopped ? <p className={s.formErr} role="alert">{`Stopped early: ${result.stopped}. Rows already saved stay saved; press Enter again to continue.`}</p> : null}
      {rows.length > 0 ? <ul aria-label="Failed rows">{rows.map((t) => <li key={t.id}><span>{`${formatDate(t.date, sc.locale, "short")} · ${t.description.slice(0, 60)} · ${sc.money(t.amount)}`}</span><span className={m.reason}>{failed.get(t.id)}</span></li>)}</ul> : null}
      <div className={s.actions}>
        {result.created > 0 ? <Button as={Link} to="/expenses" variant="primary" size="sm">View expenses</Button> : null}
        <Button size="sm" onClick={onClose}>Dismiss</Button>
      </div>
    </div>
  );
}
